import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Refresh as RefreshIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Aggregate subscriber counts only — no phone / Aadhaar dump.
 */
const MembershipSubscribersAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/membership/admin/subscribers`, {
        headers: authHeaders(),
      });
      if (res.data?.success) setData(res.data.data || {});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load subscriber stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Membership subscribers - Loksy';
    load();
  }, [load]);

  const counts = data?.counts || {};
  const usage = data?.usage || {};
  const near = data?.shopsNearCap || [];

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Button
              component={RouterLink}
              to="/content/membership-plans"
              startIcon={<ArrowBackIcon />}
              sx={{ mb: 1 }}
            >
              Plans
            </Button>
            <Typography variant="h5" fontWeight={800}>
              Subscription counts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aggregates only. No phone or Aadhaar on this page. Month {usage.yearMonth || data?.yearMonth || '—'}.
            </Typography>
          </Box>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          Free = listing only. Trial = the free signup demo (default: Prime for 30 days). Near-cap list
          shows shop name and % only.
        </Alert>

        {loading ? (
          <Box py={6} textAlign="center">
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Paper sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={3} flexWrap="wrap">
                {[
                  ['Free', counts.free],
                  ['Trial', counts.trial],
                  ['Basic', counts.basic],
                  ['Advance', counts.advance],
                  ['Prime', counts.prime],
                  ['Trial ending 7d', counts.trialEnding7d],
                ].map(([label, n]) => (
                  <Box key={label} sx={{ minWidth: 120 }}>
                    <Typography variant="h4" fontWeight={800}>
                      {n ?? 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="h6" fontWeight={700} mb={1}>
                This month volume
              </Typography>
              <Typography variant="body2">
                Bills+tokens used (all shops): <strong>{usage.billsUsed ?? 0}</strong>
              </Typography>
              <Typography variant="body2">
                Ads used (all shops): <strong>{usage.adsUsed ?? 0}</strong>
              </Typography>
              <Typography variant="body2">
                Shops at ≥{data?.alertThresholdPercent ?? 75}% cap:{' '}
                <strong>{usage.shopsNearCap ?? near.length}</strong>
              </Typography>
            </Paper>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="h6" fontWeight={700} mb={1}>
                Shops near cap
              </Typography>
              {near.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None this month.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Shop</TableCell>
                      <TableCell align="right">Peak %</TableCell>
                      <TableCell align="right">Bills %</TableCell>
                      <TableCell align="right">Ads %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {near.map((row, i) => (
                      <TableRow key={`${row.shopName}-${i}`}>
                        <TableCell>{row.shopName}</TableCell>
                        <TableCell align="right">{row.peakPercent}</TableCell>
                        <TableCell align="right">{row.billsPercent}</TableCell>
                        <TableCell align="right">{row.adsPercent}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Stack>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default MembershipSubscribersAdmin;
